import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Tag, User } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getBlogPost, getBlogList, fileUrl } from "../api";

const fmt = (d) => {
  try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setNotFound(false);
    getBlogPost(slug)
      .then((p) => {
        setPost(p);
        if (p.seo_title) document.title = p.seo_title;
      })
      .catch(() => setNotFound(true));
    getBlogList().then((all) => setRelated(all.filter((x) => x.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-[160px] pb-32 text-center">
          <h1 className="text-3xl font-extrabold text-[#0D1B2A]">Article not found</h1>
          <button onClick={() => navigate("/blog")} className="mt-6 text-[#0EA5E9] font-semibold">← Back to Blog</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-[160px] pb-32 text-center text-[#475569]">Loading article…</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Header block */}
      <section className="pt-[120px] pb-10" style={{ background: "linear-gradient(135deg,#050C17,#0D1B2A)" }}>
        <div className="pp-container max-w-3xl">
          <button onClick={() => navigate("/blog")} className="flex items-center gap-2 text-white/70 hover:text-[#10B981] text-sm mb-6">
            <ArrowLeft size={16} /> Back to Blog
          </button>
          <span className="inline-block bg-[#06B6D4] text-white text-xs font-semibold px-3 py-1 rounded-full">{post.category}</span>
          <h1 className="mt-4 text-3xl md:text-[42px] font-extrabold text-white leading-tight">{post.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-5 text-white/70 text-sm">
            <span className="flex items-center gap-2"><User size={15} /> {post.author}</span>
            <span className="flex items-center gap-2"><Calendar size={15} /> {fmt(post.published_date)}</span>
          </div>
        </div>
      </section>

      {/* Cover */}
      {post.cover_image && (
        <div className="pp-container max-w-3xl -mt-2">
          <img src={fileUrl(post.cover_image)} alt={post.title} className="w-full h-72 md:h-96 object-cover rounded-3xl shadow-xl mt-8" />
        </div>
      )}

      {/* Body */}
      <article className="pp-container max-w-3xl py-12">
        <div className="pp-article" dangerouslySetInnerHTML={{ __html: post.body || "" }} />

        {post.tags && post.tags.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center gap-2">
            <Tag size={16} className="text-[#06B6D4]" />
            {post.tags.map((t) => (
              <span key={t} className="bg-[#F0FDFA] text-[#0D1B2A] text-xs font-semibold px-3 py-1 rounded-full">{t}</span>
            ))}
          </div>
        )}
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#f6f9fd] py-16">
          <div className="pp-container">
            <h2 className="text-2xl font-extrabold text-[#0D1B2A] mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-7">
              {related.map((r) => (
                <article key={r.slug} onClick={() => navigate(`/blog/${r.slug}`)} className="group bg-white rounded-3xl overflow-hidden pp-lift border border-slate-100 cursor-pointer">
                  <div className="h-44 overflow-hidden">
                    <img src={fileUrl(r.cover_image)} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <span className="text-[11px] font-semibold text-[#06B6D4]">{r.category}</span>
                    <h3 className="mt-1 font-bold text-[#0D1B2A] leading-snug line-clamp-2 group-hover:text-[#0EA5E9]">{r.title}</h3>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
      <Footer />
    </div>
  );
};

export default BlogArticle;
